import apiClient from './client';
import { categorySegment } from '../types/conceptualProblems';
import type {
  ConceptualCategoriesResponse,
  ConceptualCategory,
  ConceptualProblemsResponse,
  SaveCategoriesResponse,
} from '../types/conceptualProblems';

/**
 * Data calls for the "Conceptual Problems" page (feature 029).
 *
 * Two reads and two writes. This module was read-only until the 2026-09-10 amendment, and its
 * header used to say so — the writes were added by the client, not drifted into. What is still
 * true is that there is no submit, no attempt and no history call here: an editor filing a problem
 * under a topic is an editorial act, not an answer to it.
 */
export const conceptualProblemsApi = {
  /**
   * Every conceptual category with its problem count, ascending by id. Entirely data-driven — a
   * category added to the table appears here with no change to the website.
   */
  getCategories: async (): Promise<ConceptualCategoriesResponse> => {
    const response = await apiClient.get<ConceptualCategoriesResponse>(
      '/conceptual-problems/categories',
    );
    return response.data;
  },

  /**
   * One category's problems, ascending by serial number.
   *
   * The WHOLE category comes back in a single call: the largest holds 212 problems, and loading
   * it once is what makes stepping between them a state change rather than a round trip.
   */
  getProblems: async (categoryId: number | null): Promise<ConceptualProblemsResponse> => {
    // skipRedirectOn404 lets the page render its own "that category no longer exists" notice: a
    // category id that names nothing is a 404, and without the opt-out the shared interceptor
    // sends the whole browser to the generic error page before useConceptualCategory can see it.
    const response = await apiClient.get<ConceptualProblemsResponse>(
      `/conceptual-problems/categories/${categorySegment(categoryId)}/problems`,
      { skipRedirectOn404: true } as never,
    );
    return response.data;
  },

  /**
   * Re-file a problem: send the COMPLETE set of categories it should now be in, optionally with
   * one to create first.
   *
   * The whole set rather than a delta, which is what makes two editors saving at once resolve as
   * "the last one wins, whole" instead of as a merge neither chose. An empty array is a real
   * request — the problem becomes Uncategorised (FR-031) — not a no-op to guard against.
   *
   * skipRedirectOn404 for the same reason the read has it: a problem or category that has gone
   * away while the picker was open must surface as a message on this page, not as a jump to the
   * generic error page with no way back.
   *
   * skipForbiddenRedirect because a 403 here is usually not the feature gate: it is a problem a
   * Super Editor has locked (`code: 'PROBLEM_LOCKED'`, FR-048), and the editor is owed that sentence
   * on the problem they are reading, not a trip to /forbidden. A genuine gate 403 cannot reach this
   * call — the page it lives on is behind the same gate — so nothing is lost by opting out.
   */
  saveProblemCategories: async (
    serial: number,
    categoryIds: number[],
    newCategoryName?: string | null,
  ): Promise<SaveCategoriesResponse> => {
    const response = await apiClient.put<SaveCategoriesResponse>(
      `/conceptual-problems/problems/${serial}/categories`,
      { categoryIds, newCategoryName: newCategoryName ?? null },
      { skipRedirectOn404: true, skipForbiddenRedirect: true } as never,
    );
    return response.data;
  },

  /**
   * Create one category from the category list. Comes back with a count of zero, which the list
   * shows rather than hiding (FR-034).
   */
  createCategory: async (name: string): Promise<ConceptualCategory> => {
    const response = await apiClient.post<ConceptualCategory>(
      '/conceptual-problems/categories',
      { name },
    );
    return response.data;
  },
};
